/**
 * Facebook Marketplace channel panel (listing-marketing-hub US-5, BR-8, BR-12): post with the
 * extension; inquiries arrive in Messenger, not email, so the landlord replies — Option A a canned
 * reply to copy (listing-page-link or Resida-email variant), Option B connect an AI assistant in
 * Connected Apps; then record the post. Instruction text is kept in step with the
 * market-on-facebook-marketplace Help article.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Disclosure } from '../../layout/Disclosure';
import { buttonVariants } from '../../ui/button-variants';
import { cn } from '../../../lib/utils';
import { buildMessengerReply, type MessengerReplyVariant } from '../../../lib/listing-marketing';
import { CopyValueBox } from '../../../pages/leasing/listing-detail/ShareCopyButtons';
import { GuideLastChecked, HelpGuideLink } from './HelpGuideLink';
import { Callout, PostingControls, StepList } from './StepList';

export const FACEBOOK_GUIDE_ARTICLE_ID = 'market-on-facebook-marketplace';

const VARIANTS: Array<{ key: MessengerReplyVariant; label: string }> = [
  { key: 'page', label: 'Listing page link' },
  { key: 'email', label: 'Resida email' },
];

interface FacebookPanelProps {
  publicUrl: string | null;
  intakeAddress: string | null;
  /** How the renter knows the place, for the canned reply. */
  address: string;
  /** The Post to Facebook Marketplace action, the extension capability note, or the posting-off note. */
  postAction: ReactNode;
  /** The Facebook record-a-post form; null while posting is off. */
  recordForm: ReactNode;
  /** The listing is paused or closed: posting and copying are off, the guide stays readable (BR-1). */
  postingOff: boolean;
  /** Posted Facebook Marketplace records on this listing. */
  postedCount: number;
}

export function FacebookPanel({
  publicUrl,
  intakeAddress,
  address,
  postAction,
  recordForm,
  postingOff,
  postedCount,
}: FacebookPanelProps) {
  const [variant, setVariant] = useState<MessengerReplyVariant>('page');
  const reply = buildMessengerReply(variant, { publicUrl, intakeEmail: intakeAddress, address });

  return (
    <section
      aria-labelledby="marketing-facebook-title"
      data-testid="marketing-panel-facebook"
      className="flex min-w-0 flex-1 flex-col gap-5 rounded-xl border border-border bg-card p-[var(--card-padding)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 id="marketing-facebook-title" className="text-lg font-semibold text-foreground">
            Facebook Marketplace
          </h2>
          <p className="text-sm text-muted-foreground">Replies arrive in Messenger, not email.</p>
        </div>
        <HelpGuideLink articleId={FACEBOOK_GUIDE_ARTICLE_ID} testId="facebook-guide-link" />
      </div>

      <StepList
        testId="facebook-steps"
        steps={[
          {
            title: 'Post the listing',
            children: (
              <>
                <p className="text-sm text-muted-foreground">
                  Opens Facebook&apos;s rental form already filled from this listing, photos included. You review it
                  and press Post.
                </p>
                {postAction}
              </>
            ),
          },
          {
            title: 'Answer inquiries from Messenger',
            children: (
              <>
                <Callout title="Facebook replies do not come to your email." testId="facebook-messenger-callout">
                  Every inquiry lands in Messenger, where Resida cannot see it, so you reply to each one yourself. Pick
                  one way to make that quick.
                </Callout>

                <PostingControls off={postingOff}>
                  <div className="flex flex-col gap-3 rounded-lg border border-border p-4" data-testid="facebook-option-a">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-foreground">Option A · Paste a saved reply</h4>
                      <div role="group" aria-label="Reply points renters to" className="flex flex-wrap gap-2">
                        {VARIANTS.map((v) => (
                          <button
                            key={v.key}
                            type="button"
                            aria-pressed={variant === v.key}
                            onClick={() => setVariant(v.key)}
                            data-testid={`facebook-reply-variant-${v.key}`}
                            className={cn(
                              buttonVariants({ variant: variant === v.key ? 'default' : 'outline', size: 'sm' }),
                              'min-h-[44px]',
                            )}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <CopyValueBox
                      label="Reply"
                      value={reply}
                      emptyText=""
                      testIdPrefix="facebook-reply"
                      multiline
                      copyLabel="Copy reply"
                      caption="Paste it into each Messenger inquiry."
                    />
                  </div>

                  <div className="flex flex-col gap-2 rounded-lg border border-border p-4" data-testid="facebook-option-b">
                    <h4 className="text-sm font-semibold text-foreground">Option B · Let an AI assistant help</h4>
                    <p className="text-sm text-muted-foreground">
                      Connect your AI assistant to Resida so it can answer questions about this listing and help you
                      reply.
                    </p>
                    <div>
                      <Link
                        to="/settings/connected-apps"
                        data-testid="facebook-connect-assistant"
                        className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary underline underline-offset-4"
                      >
                        Connect an assistant
                      </Link>
                    </div>
                  </div>
                </PostingControls>

                <Disclosure title="Why can't Resida see my Facebook inquiries?" testId="facebook-why-disclosure" headingLevel={4}>
                  <p className="text-sm text-foreground">
                    Facebook sends Marketplace inquiries to Messenger instead of an email address, so they never reach
                    your Resida email and Resida cannot read or answer them. A saved reply or a connected assistant is
                    the quickest way to answer each one.
                  </p>
                </Disclosure>
              </>
            ),
          },
          {
            title: 'Record your post',
            children: (
              <>
                <p className="text-sm text-muted-foreground" data-testid="facebook-record-copy">
                  {postingOff
                    ? 'Posting is off, but you can still record a post you made yourself with the form above. Take each post down on Facebook, then mark it removed.'
                    : "The extension records it for you when it sees the post go live. Otherwise paste the post's web address here so Resida can remind you to take it down later."}
                </p>
                {recordForm}
                <p className="text-xs text-muted-foreground" data-testid="facebook-recorded-count">
                  {postedCount === 0
                    ? 'No Facebook posts recorded yet.'
                    : `${postedCount} Facebook ${postedCount === 1 ? 'post' : 'posts'} recorded.`}
                </p>
              </>
            ),
          },
        ]}
      />

      <GuideLastChecked articleId={FACEBOOK_GUIDE_ARTICLE_ID} testId="facebook-guide-checked" />
    </section>
  );
}
